import { useQuery } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * "Conectar meu Slack": cada pessoa liga o próprio Slack ao login do WMS com
 * um clique (Entrar com o Slack). A conexão do BOT é do app e fica no
 * servidor; esta é só para saber quem é quem — o bot marca a pessoa na nota e
 * a resposta dela na thread age com as permissões dela.
 */
export function MeuSlack(): JSX.Element | null {
  const eu = useQuery<{ conectado: boolean; comoPessoa: boolean; nome: string | null; disponivel: boolean }>({
    queryKey: ['slack-eu'],
    queryFn: () => api.get('/api/slack/eu'),
  });
  if (!eu.data) return null;
  if (eu.data.conectado) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full bg-status-livreFundo px-3 py-1.5 text-xs font-bold text-status-livre"
        title={
          eu.data.comoPessoa
            ? 'Seu Slack está ligado: o que você escreve na nota sai no Slack como você.'
            : 'Seu Slack está ligado, mas suas mensagens ainda saem pelo bot. Autorize para saírem como você.'
        }
      >
        <LogoSlack className="h-4 w-4" /> {eu.data.nome ?? 'Slack conectado'} <CheckCircle2 className="h-4 w-4" />
        {eu.data.comoPessoa || !eu.data.disponivel ? (
          eu.data.disponivel ? (
            <a className="ml-1 font-semibold underline-offset-2 hover:underline" href="/api/slack/conectar">
              trocar
            </a>
          ) : null
        ) : (
          <a className="ml-1 rounded-full bg-white px-2 py-0.5 font-semibold text-[#1d1c1d] shadow-xs hover:bg-[#f8f8f8]" href="/api/slack/conectar">
            Postar como eu
          </a>
        )}
      </span>
    );
  }
  if (!eu.data.disponivel) return null;
  return (
    <a
      href="/api/slack/conectar"
      className="inline-flex h-9 items-center gap-2.5 rounded-lg border border-[#d0d0d0] bg-white px-3.5 text-[13px] font-semibold text-[#1d1c1d] shadow-xs transition hover:bg-[#f8f8f8] hover:shadow-sm"
      title="Liga o seu Slack ao seu usuário do WMS: o bot passa a marcar você nas notas e as suas respostas na thread agem na nota."
    >
      <LogoSlack className="h-[18px] w-[18px]" />
      Conectar com o Slack
    </a>
  );
}

/** O logo do Slack nas quatro cores oficiais. */
export function LogoSlack({ className }: { className?: string }): JSX.Element {
  return (
    <svg viewBox="0 0 122.8 122.8" className={className} aria-hidden>
      <path fill="#E01E5A" d="M25.8 77.6a12.9 12.9 0 1 1-12.9-12.9h12.9zm6.5 0a12.9 12.9 0 0 1 25.8 0v32.3a12.9 12.9 0 1 1-25.8 0z" />
      <path fill="#36C5F0" d="M45.2 25.8a12.9 12.9 0 1 1 12.9-12.9v12.9zm0 6.5a12.9 12.9 0 0 1 0 25.8H12.9a12.9 12.9 0 0 1 0-25.8z" />
      <path fill="#2EB67D" d="M97 45.2a12.9 12.9 0 1 1 12.9 12.9H97zm-6.5 0a12.9 12.9 0 0 1-25.8 0V12.9a12.9 12.9 0 1 1 25.8 0z" />
      <path fill="#ECB22E" d="M77.6 97a12.9 12.9 0 1 1-12.9 12.9V97zm0-6.5a12.9 12.9 0 0 1 0-25.8h32.3a12.9 12.9 0 0 1 0 25.8z" />
    </svg>
  );
}

/** A mensagem de volta do Slack (?slack=...), em português. */
export const RETORNO_SLACK: Record<string, { texto: string; ok: boolean }> = {
  conectado: { texto: 'Pronto: seu Slack está ligado ao seu usuário do WMS.', ok: true },
  cancelado: { texto: 'Conexão com o Slack cancelada.', ok: false },
  invalido: { texto: 'O retorno do Slack expirou ou é de outra sessão. Tente de novo.', ok: false },
  outro_workspace: { texto: 'Esse Slack é de outro workspace. Use o da Megamamute.', ok: false },
  erro: { texto: 'O Slack não confirmou a conexão. Tente de novo.', ok: false },
};
